import React, { useState } from "react";
import { Mars, Venus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const GENDERS = {
    male: { label: "Male", Icon: Mars, cls: "text-sky-600", bg: "bg-sky-50 border-sky-200 text-sky-700" },
    female: { label: "Female", Icon: Venus, cls: "text-pink-600", bg: "bg-pink-50 border-pink-200 text-pink-700" },
};

export const GenderIcon = ({ gender, className = "h-3.5 w-3.5", testid }) => {
    const g = GENDERS[gender];
    if (!g) return null;
    return <g.Icon className={`${className} ${g.cls}`} aria-label={g.label} data-testid={testid} />;
};

export const genderCounts = (list) => {
    const c = { total: list.length, male: 0, female: 0, unset: 0 };
    for (const u of list) c[GENDERS[u.gender] ? u.gender : "unset"] += 1;
    return c;
};

// Read-only chip for everyone; dropdown for super admins.
export const GenderPicker = ({ u, canEdit, onSaved, size = "sm" }) => {
    const [busy, setBusy] = useState(false);
    const g = GENDERS[u.gender];
    const h = size === "sm" ? "h-7 text-xs" : "h-8 text-sm";
    if (!canEdit) {
        return g ? (
            <span className={`inline-flex items-center gap-1.5 px-2.5 rounded-full border ${h} ${g.bg}`} data-testid={`gender-chip-${u.user_id}`}>
                <g.Icon className="h-3.5 w-3.5" />{g.label}
            </span>
        ) : <span className="text-slate-300 text-sm" data-testid={`gender-chip-${u.user_id}`}>—</span>;
    }
    const save = async (val) => {
        setBusy(true);
        try {
            await api.put(`/users/${u.user_id}`, { gender: val });
            await onSaved?.({ gender: val || null });
            toast.success(val ? `Set to ${GENDERS[val].label}` : "Gender cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save gender");
        } finally {
            setBusy(false);
        }
    };
    return (
        <span className={`relative inline-flex items-center rounded-full border ${h} ${g ? g.bg : "bg-white border-dashed border-slate-300 text-slate-500"}`}>
            {g && <g.Icon className="h-3.5 w-3.5 absolute left-2 pointer-events-none" />}
            <select
                value={u.gender || ""}
                disabled={busy}
                onChange={(e) => save(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`gender-select-${u.user_id}`}
                className={`appearance-none bg-transparent outline-none cursor-pointer font-medium ${g ? "pl-7" : "pl-2.5"} pr-3 h-full rounded-full disabled:opacity-50`}
            >
                <option value="">Not set</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
            </select>
        </span>
    );
};

export const GenderRatioBar = ({ c, className = "" }) => {
    const set = c.male + c.female;
    if (!set) return null;
    return (
        <div className={`flex h-1.5 w-full rounded-full overflow-hidden bg-slate-100 ${className}`} title={`Male ${c.male} · Female ${c.female}`}>
            <div className="bg-sky-500" style={{ width: `${(c.male * 100) / set}%` }} />
            <div className="bg-pink-500" style={{ width: `${(c.female * 100) / set}%` }} />
        </div>
    );
};

export const DemographicsStrip = ({ list }) => {
    const c = genderCounts(list);
    const set = c.male + c.female;
    const pct = (n) => (set ? Math.round((n * 100) / set) : 0);
    const tiles = [
        ["total", "Total", c.total, null, "text-slate-900"],
        ["male", "Male", c.male, set ? `${pct(c.male)}%` : null, "text-sky-700"],
        ["female", "Female", c.female, set ? `${pct(c.female)}%` : null, "text-pink-700"],
        ["unset", "Not set", c.unset, null, "text-slate-500"],
    ];
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4" data-testid="demographics-strip">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Demographics</div>
                {set > 0 && <div className="text-xs text-slate-500" data-testid="demographics-ratio">Male : Female = <span className="font-semibold text-slate-800">{c.male} : {c.female}</span></div>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {tiles.map(([k, l, v, sub, cls]) => (
                    <div key={k} className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`demographics-${k}`}>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">{GENDERS[k] && <GenderIcon gender={k} className="h-3 w-3" />}{l}</div>
                        <div className={`text-xl font-bold tabular-nums ${cls}`}>{v}{sub && <span className="ml-1.5 text-xs font-medium text-slate-400">{sub}</span>}</div>
                    </div>
                ))}
            </div>
            <GenderRatioBar c={c} className="mt-3" />
        </div>
    );
};

export const TeamGenderPill = ({ members, testid }) => {
    const seen = new Set();
    const c = genderCounts(members.filter((m) => !seen.has(m.user_id) && seen.add(m.user_id)));
    if (!c.male && !c.female) return null;
    return (
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-white border-slate-200 text-slate-600" title="Male : Female" data-testid={testid}>
            <Mars className="h-3 w-3 text-sky-600" />{c.male}
            <span className="text-slate-300">:</span>
            <Venus className="h-3 w-3 text-pink-600" />{c.female}
        </span>
    );
};
